import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getLocalTimezone, zonedWallToUtc } from "@/lib/eventDatetime";
import type { ReminderOffset } from "../types";
import { breakfastEventsPrefix, pendingInvitesPrefix } from "../queryKeys";
import { circlesSchedulePrefix } from "@/features/circles/queryKeys";
import { trackedDaysPrefix } from "@/hooks/queries/useTrackedDays";
import { describeEventInviteError } from "../lib/describeEventInviteError";

/**
 * Update an existing breakfast event (composite save).
 *
 * A single logical "save" actually fans out to several tables now that
 * `potluck_items` is a first-class relation. Each DB call is awaited in
 * sequence and any failure is *explicitly* re-thrown as
 * `new Error(supabaseError.message)` so React Query reliably routes the
 * failure into `onError` instead of silently invoking `onSuccess` — that
 * was the "Fake Success" bug the user reported.
 *
 * ## Fan-out
 *
 *   1. UPDATE breakfast_events   — native columns only. `potluck_items`
 *      is NEVER in this payload; the column was dropped in phase 23.
 *   2. INSERT new invitees into breakfast_attendees as `pending`
 *      (idempotent via composite unique `(event_id, user_id)`).
 *   3. Reconcile potluck_items against the desired list (insert new,
 *      delete removed; existing rows retain their `claimed_by`).
 *   4. Best-effort pin of tracked_days for the host on the new date.
 *
 * Every write that matters asserts a post-condition via `.select()` and
 * row-count checks. Supabase's JS SDK returns `{ error: null }` even when
 * RLS silently matches zero rows; without the assertion the caller can't
 * distinguish "updated" from "blocked". That is why the UI was showing
 * a success toast but never actually changing the DB. See also
 * `useDeleteEvent`, same trap.
 *
 * NOTE: The legacy SECURITY DEFINER RPC also (a) sent in-app invite
 * notifications and (b) reset invitees' RSVPs to `pending` on reschedule.
 * Both mutate rows the host doesn't own and are blocked by RLS from the
 * client. If they are still desired, implement them as AFTER UPDATE
 * triggers on `breakfast_events`.
 */
export function useUpdateEventFull() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: {
      eventId: string;
      title: string;
      type: "potluck" | "outside";
      eventTag: string;
      /** Host-selected glyph key — see `lib/eventIcons.ts`. `undefined` leaves it unchanged. */
      icon?: string;
      location?: string;
      /**
       * Desired final state of potluck items. Items with an `id` that already
       * exists in the DB are preserved (keeping their `claimed_by`); items
       * with new/unknown ids are inserted; existing DB rows missing from this
       * list are deleted. Pass `undefined` to leave items untouched.
       */
      potluckItems?: Array<{ id?: string; item_name: string }>;
      inviteeIds?: string[];
      date: string;
      time: string;
      ends: string;
      /**
       * Updated reminder offset. `null` clears the reminder,
       * `undefined` leaves it unchanged. Allowed scalar values
       * enforced by `breakfast_events_reminder_offset_chk`.
       */
      reminderOffsetMinutes?: ReminderOffset | null;
    }) => {
      // ── Step 1: UPDATE breakfast_events (native columns only) ──────────
      //
      // `payload` is pulled out and `console.debug`'d *before* the wire
      // call so we can verify in the browser console that the form
      // actually propagated `event_time` / `event_ends` / `event_date` —
      // previous "fake success" reports hinted the form might be posting
      // stale values. Grep the console for `[updateEventFull] payload`
      // while reproducing to see exactly what hits Supabase.
      //
      // Phase 24: recompute UTC instants on every save. The form still
      // submits a wall-clock `date` + `time` + `ends` triple, but the DB's
      // source of truth for the moment is `starts_at` / `ends_at`. We use
      // the viewer's CURRENT zone as the author zone so edits made while
      // travelling correctly re-anchor the event — matching Outlook's
      // "Event will move to your current timezone" behaviour. (If a
      // future iteration wants to preserve the original host zone on
      // edit, that becomes a separate UI control.)
      const hostTz = getLocalTimezone();
      const startsAt = zonedWallToUtc(params.date, params.time, hostTz);
      const endsAt = params.ends
        ? zonedWallToUtc(params.date, params.ends, hostTz)
        : null;

      const payload: Record<string, unknown> = {
        title:            params.title,
        event_type:       params.type,
        event_tag:        params.eventTag,
        ...(params.icon ? { icon: params.icon } : {}),
        location_or_link: params.location || null,
        event_date:       params.date,
        event_time:       params.time + ":00",
        event_ends:       params.ends + ":00",
        starts_at:        startsAt,
        ends_at:          endsAt,
        event_timezone:   hostTz,
      };
      // Only write `reminder_offset_minutes` when the caller actually
      // supplied the field so a partial edit doesn't accidentally nuke
      // a preference they didn't touch. `null` means "explicitly clear"
      // and is included.
      if (params.reminderOffsetMinutes !== undefined) {
        payload.reminder_offset_minutes = params.reminderOffsetMinutes;
      }
      console.debug("[updateEventFull] payload", {
        eventId: params.eventId,
        ...payload,
      });

      // Sprint 2 — Bugs 2 & 3 (transactional emails): snapshot the
      // event's current state BEFORE we overwrite it so we can:
      //   • detect `starts_at` / `event_date` / `event_time` changes
      //     (→ reschedule email for retained attendees), and
      //   • include the right event metadata in the cancellation
      //     email body when attendees are removed in this save.
      // This is a best-effort read — if it fails we still continue
      // with the save and just skip the corresponding emails.
      const { data: beforeEvent } = await supabase
        .from("breakfast_events")
        .select("title, event_date, event_time, event_ends, starts_at, ends_at, event_timezone, location_or_link, host_id")
        .eq("id", params.eventId)
        .maybeSingle();

      // `.select()` returns the affected rows so we can detect the
      // Supabase "silent success" where `error` is null but RLS filtered
      // every row out. Without this, the mutation would reach `onSuccess`
      // even though nothing was actually written.
      const { data: updatedRows, error: eventError } = await supabase
        .from("breakfast_events")
        .update(payload)
        .eq("id", params.eventId)
        .select();

      if (eventError) throw new Error(eventError.message);
      if (!updatedRows || updatedRows.length === 0) {
        throw new Error(
          "Event could not be updated. You may not be the host, or the event no longer exists.",
        );
      }

      // Track who was removed / added in this save so we can fire
      // transactional emails AFTER all DB writes have succeeded.
      // Declared at function scope so the post-save email block can
      // close over them regardless of whether step 2 runs.
      let removedUserIds: string[] = [];
      let addedUserIds:   string[] = [];
      let retainedUserIds: string[] = [];

      // ── Step 2: Reconcile invitees (ADD new + REMOVE unchecked) ────────
      //
      // Sprint 2 — Bug 12 (Member Removal Logic)
      //
      // `params.inviteeIds` is the DESIRED final invitee list (host
      // excluded). We diff it against the current attendee roster:
      //
      //   • Any id in `inviteeIds` that is NOT already on the roster
      //     is upserted as `pending` (same as before).
      //   • Any non-host id that IS on the roster but is NOT in
      //     `inviteeIds` is DELETED — that is the "host removed this
      //     person in the edit modal" case. Previously we only added,
      //     which left ghost rows on the event and silently kept the
      //     removed user's calendar entry.
      //
      // Passing `inviteeIds: undefined` leaves the roster alone (used
      // by any future partial-save path).
      if (params.inviteeIds !== undefined) {
        const updatedEvent = updatedRows[0] as { host_id?: string } | undefined;
        const hostId = updatedEvent?.host_id ?? null;

        const { data: currentRows, error: currentErr } = await supabase
          .from("breakfast_attendees")
          .select("user_id")
          .eq("event_id", params.eventId);
        if (currentErr) throw new Error(currentErr.message);

        const currentIds = new Set(
          (currentRows ?? [])
            .map((r: { user_id: string }) => r.user_id)
            .filter((id) => id !== hostId),
        );
        const desiredIds = new Set(params.inviteeIds.filter((id) => id !== hostId));

        const toAdd = [...desiredIds].filter((id) => !currentIds.has(id));
        const toRemove = [...currentIds].filter((id) => !desiredIds.has(id));
        const retained = [...currentIds].filter((id) => desiredIds.has(id));

        addedUserIds    = toAdd;
        removedUserIds  = toRemove;
        retainedUserIds = retained;

        if (toAdd.length > 0) {
          const rows = toAdd.map((userId) => ({
            event_id: params.eventId,
            user_id: userId,
            status: "pending" as const,
          }));
          const { error: inviteeError } = await supabase
            .from("breakfast_attendees")
            .upsert(rows, {
              onConflict: "event_id,user_id",
              ignoreDuplicates: true,
            });
          if (inviteeError) throw new Error(inviteeError.message);
        }

        if (toRemove.length > 0) {
          // Outlook-style soft removal.
          //
          // Before April 2026 this was a hard `DELETE FROM
          // breakfast_attendees`. That DELETE caused the "ghost-event
          // 403" UX bug: the removed user's React Query cache still had
          // the event until the next refetch, and during that window any
          // interaction (toggling a potluck item) returned an opaque
          // HTTP 403 because the attendee row was gone server-side.
          //
          // The new `soft_remove_breakfast_attendees` RPC instead flips
          // each attendee row to `status='removed'` AND inserts an
          // `EVENT_CANCELLED` bell notification for every removed user
          // — both side effects in a single round-trip. The attendee's
          // calendar keeps rendering the event with the "cancelled"
          // visual state, potluck claims are server-side blocked, and
          // the only way to truly clear the event is the user-tap
          // "Remove from calendar" bell action (→
          // `remove_event_from_my_calendar`).
          const { data: removedCount, error: removeErr } = await supabase.rpc(
            "soft_remove_breakfast_attendees",
            {
              p_event_id: params.eventId,
              p_user_ids: toRemove,
            },
          );
          if (removeErr) throw new Error(removeErr.message);
          if (
            typeof removedCount === "number" &&
            removedCount < toRemove.length
          ) {
            // The RPC may legitimately affect fewer rows than requested
            // when a user was already in `status='removed'` — that's
            // idempotent and fine. We only warn on a truly unexpected
            // mismatch that would indicate a data inconsistency.
            console.warn(
              `[useUpdateEventFull] soft_remove_breakfast_attendees: requested ${toRemove.length} but server reported ${removedCount}. ` +
                "(Some users may already have been in the removed state.)",
            );
          }
        }
      }

      // ── Step 3: Reconcile potluck_items (diff-based) ───────────────────
      if (params.potluckItems !== undefined) {
        const { data: existing, error: fetchErr } = await supabase
          .from("potluck_items")
          .select("id")
          .eq("event_id", params.eventId);
        if (fetchErr) throw new Error(fetchErr.message);

        const existingIds = new Set(
          (existing || []).map((r: { id: string }) => r.id),
        );
        const desiredIds = new Set(
          params.potluckItems
            .filter((it) => it.id && existingIds.has(it.id))
            .map((it) => it.id as string),
        );

        // Newly added items: either no id (client-generated) or an id we
        // don't have on the server. We materialize them into the new
        // `potluck_items` table — this is the composite half of the save
        // that the old JSONB flow used to do inline.
        const toInsert = params.potluckItems
          .filter((it) => !it.id || !existingIds.has(it.id))
          .map((it) => ({
            event_id: params.eventId,
            item_name: it.item_name.trim(),
          }))
          .filter((r) => r.item_name.length > 0);

        const toDelete = [...existingIds].filter((id) => !desiredIds.has(id));

        if (toDelete.length > 0) {
          const { error: deleteItemsError } = await supabase
            .from("potluck_items")
            .delete()
            .in("id", toDelete);
          if (deleteItemsError) throw new Error(deleteItemsError.message);
        }

        if (toInsert.length > 0) {
          const { data: insertedItems, error: insertItemsError } = await supabase
            .from("potluck_items")
            .insert(toInsert)
            .select("id");
          if (insertItemsError) throw new Error(insertItemsError.message);

          // Same "fake success" guard as above — if RLS silently filtered
          // the insert (e.g. caller not a host/attendee of the event), the
          // response has error=null but no rows.
          if (!insertedItems || insertedItems.length !== toInsert.length) {
            throw new Error(
              "Some potluck items could not be saved. Please try again.",
            );
          }
        }
      }

      // ── Step 4: Pin tracked_days for the host (best effort) ────────────
      const { data: { session } } = await supabase.auth.getSession();
      const currentUserId = session?.user?.id;

      if (currentUserId) {
        const { data: trackData } = await supabase
          .from("tracked_days")
          .select("id")
          .eq("user_id", currentUserId)
          .eq("date", params.date)
          .maybeSingle();

        if (!trackData) {
          await supabase.from("tracked_days").insert({
            user_id: currentUserId,
            date: params.date,
            system_day_type_id: "office",
            status: "unconfirmed",
          });
        }
      }

      // ── Step 5: Transactional emails (Sprint 2 — Bugs 2 & 3) ───────────
      //
      // All DB writes have succeeded by this point. Any failure below is
      // a delivery problem, not a data problem — we must NOT throw out
      // of the mutation or the user sees a false "Save failed" toast
      // for a perfectly-committed change. Each branch is wrapped in
      // its own try/catch + console.warn.
      //
      // Three branches, in dependency order:
      //   5a. Cancellation email → users REMOVED in this save.
      //   5b. Invite email       → users ADDED in this save.
      //   5c. Reschedule email   → users RETAINED, when the date/time
      //                            actually changed vs `beforeEvent`.
      const hostId = (beforeEvent?.host_id as string | undefined) ?? currentUserId ?? null;

      // If the caller didn't touch the roster (inviteeIds === undefined)
      // we still want reschedule emails for the existing attendees, so
      // fetch the full roster minus the host for the reschedule step.
      if (params.inviteeIds === undefined && hostId) {
        const { data: rosterRows } = await supabase
          .from("breakfast_attendees")
          .select("user_id")
          .eq("event_id", params.eventId);
        retainedUserIds = (rosterRows ?? [])
          .map((r: { user_id: string }) => r.user_id)
          .filter((id) => id !== hostId);
      }

      // 5a. Cancellation for removed attendees.
      if (removedUserIds.length > 0 && hostId) {
        try {
          const { error: cancelErr } = await supabase.functions.invoke(
            "send-event-cancellation",
            {
              body: {
                removed_user_ids: removedUserIds,
                reason: "removed",
                event: {
                  title:            beforeEvent?.title ?? params.title,
                  event_date:       beforeEvent?.event_date ?? params.date,
                  event_time:       beforeEvent?.event_time ?? null,
                  event_ends:       beforeEvent?.event_ends ?? null,
                  starts_at:        beforeEvent?.starts_at ?? null,
                  ends_at:          beforeEvent?.ends_at ?? null,
                  event_timezone:   beforeEvent?.event_timezone ?? hostTz,
                  location_or_link: beforeEvent?.location_or_link ?? null,
                  host_id:          hostId,
                },
              },
            },
          );
          if (cancelErr) {
            console.warn("[useUpdateEventFull] send-event-cancellation failed:", cancelErr.message);
          }
        } catch (err) {
          console.warn("[useUpdateEventFull] send-event-cancellation threw:", err);
        }
      }

      // 5b. Invite for newly added attendees.
      const emailWarnings: string[] = [];
      if (addedUserIds.length > 0) {
        const warn = await invokeEventInvite({
          eventId:   params.eventId,
          inviteeIds: addedUserIds,
          reschedule: false,
          context:   "add",
        });
        if (warn) emailWarnings.push(warn);
      }

      // 5c. Reschedule: only when the moment actually shifted. We
      // compare `starts_at` when available (UTC-anchored, timezone-
      // safe) and fall back to the wall-clock triple otherwise. The
      // email is sent to every attendee who is STILL on the roster
      // after this save — not the just-added or just-removed ones,
      // who already got a more specific email in 5a/5b above.
      const rescheduled = (() => {
        if (!beforeEvent) return false;
        if (beforeEvent.starts_at && startsAt) {
          return new Date(beforeEvent.starts_at).getTime() !== new Date(startsAt).getTime();
        }
        return (
          beforeEvent.event_date !== params.date ||
          beforeEvent.event_time !== `${params.time}:00` ||
          beforeEvent.event_ends !== `${params.ends}:00`
        );
      })();

      if (rescheduled && retainedUserIds.length > 0) {
        const warn = await invokeEventInvite({
          eventId:    params.eventId,
          inviteeIds: retainedUserIds,
          reschedule: true,
          context:    "reschedule",
        });
        if (warn) emailWarnings.push(warn);
      }

      return { emailWarnings };
    },
    // Aggressive, foolproof cache invalidation. Three safeguards stacked:
    //
    //   1. `queryKey: breakfastEventsPrefix` invalidates by prefix (React
    //      Query v5's default `exact: false`), so EVERY dated cache slice
    //      registered as `["breakfastEvents", userId, startDate, endDate]`
    //      gets refetched. The string must match the root of the actual
    //      fetch hook — it is camelCase here because `useBreakfastEvents`
    //      registers with camelCase. Snake_case `["breakfast_events"]`
    //      would silently match nothing and reproduce the "fake success"
    //      cache staleness — which is precisely why we centralised the
    //      keys in `queryKeys.ts`.
    //
    //   2. `refetchType: "all"` forces even inactive / unmounted queries
    //      to refetch on next mount, which guards against the detail
    //      drawer/edit modal rehydrating from a stale observer that the
    //      default `refetchType: "active"` would skip.
    //
    //   3. We fire the same invalidation in BOTH `onSuccess` AND
    //      `onSettled`. `onSettled` covers the edge case where the write
    //      threw but left partial server-side effects (e.g. step 1
    //      committed, step 3 threw): we still want a single refetch to
    //      reconcile the UI with whatever the server actually persisted.
    //
    //   4. `await`ing the invalidation makes the mutation's `isPending`
    //      stay truthy until the refetch has landed, so the consumer's
    //      "Saved" toast fires against fresh data.
    onSuccess: async (result) => {
      // Surface any non-blocking email-dispatch problems so the host
      // knows when invitations / reschedule notices didn't actually go
      // out. The event itself did save — we just couldn't reach the
      // email pipeline for one or more recipients.
      if (result?.emailWarnings?.length) {
        toast({
          title:       "Some emails not sent",
          description: result.emailWarnings.join(" "),
        });
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: breakfastEventsPrefix,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: pendingInvitesPrefix,
          refetchType: "all",
        }),
        // cross-feature: event detail drawer
        queryClient.invalidateQueries({
          queryKey: ["eventDetails"],
          refetchType: "all",
        }),
        // cross-feature: circles calendar
        queryClient.invalidateQueries({
          queryKey: circlesSchedulePrefix,
          refetchType: "all",
        }),
        // cross-feature: dashboard tracked-days grid
        queryClient.invalidateQueries({
          queryKey: trackedDaysPrefix,
          refetchType: "all",
        }),
      ]);
    },
    onSettled: () => {
      // Belt-and-suspenders: runs after both success and error paths.
      // Not awaited — this is a best-effort re-reconciliation.
      queryClient.invalidateQueries({
        queryKey: breakfastEventsPrefix,
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: pendingInvitesPrefix,
        refetchType: "all",
      });
      // cross-feature: event detail drawer
      queryClient.invalidateQueries({
        queryKey: ["eventDetails"],
        refetchType: "all",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Could not update event",
        description: err?.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });
}

/**
 * Thin wrapper around `supabase.functions.invoke("send-event-invite", …)`
 * that normalizes every possible failure — network error, 404 on a
 * non-deployed function, missing RESEND_API_KEY, per-recipient Resend
 * rejection — into a user-facing warning string (or `null` when
 * everything dispatched cleanly).
 *
 * Returning `null` instead of throwing is deliberate: emails are a
 * side effect, not a hard contract. The DB writes above already
 * succeeded; we just need to tell the host if the notification
 * pipeline fell over so they can nudge their guests manually.
 */
async function invokeEventInvite(args: {
  eventId:    string;
  inviteeIds: string[];
  reschedule: boolean;
  context:    "add" | "reschedule";
}): Promise<string | null> {
  try {
    const { data, error } = await supabase.functions.invoke(
      "send-event-invite",
      {
        body: {
          event_id:    args.eventId,
          invitee_ids: args.inviteeIds,
          reschedule:  args.reschedule,
        },
      },
    );

    if (error) {
      console.error(
        `[useUpdateEventFull] send-event-invite (${args.context}) failed:`,
        error,
      );
      return describeEventInviteError(error, args.context);
    }

    const payload = (data ?? {}) as {
      success?: boolean;
      sent?:    number;
      skipped?: string;
      failed?:  Array<{ recipient_id: string; reason?: string }>;
      error?:   string;
    };
    console.log(
      `[useUpdateEventFull] send-event-invite (${args.context}) result:`,
      payload,
    );

    if (payload.skipped === "no_resend_key") {
      return "Resend is not configured — no emails were sent.";
    }
    if (payload.skipped === "event_not_found") {
      return "Email service could not locate this event.";
    }
    if (payload.error) {
      return `Email service error: ${payload.error}.`;
    }
    if (Array.isArray(payload.failed) && payload.failed.length > 0) {
      const reasons = payload.failed.map((f) => f.reason || "unknown").join(", ");
      return payload.sent && payload.sent > 0
        ? `${payload.sent} email(s) sent, ${payload.failed.length} failed (${reasons}).`
        : `No emails sent (${reasons}).`;
    }
    return null;
  } catch (err) {
    console.error(
      `[useUpdateEventFull] send-event-invite (${args.context}) threw:`,
      err,
    );
    return describeEventInviteError(err, args.context);
  }
}
